import { useState, useCallback, useEffect } from 'react';

import { SMSAutoResponse, ApplicationAccessType, CommunicationType } from 'domains/content/types';
import { DEFAULT_LANGUAGE_CODE } from 'domains/core/components/LanguageTabBar';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';

type UseSmsFormProps = {
    initialApplicationsAccess?: ApplicationAccessType[];
    initialName?: string;
    initialCommunicationType?: CommunicationType;
    initialContent?: string;
    initialAutoResponses?: SMSAutoResponse[];
    isSavedSmsContent: boolean;
    isSavedSmsContentLoading: boolean;
    initialStatus?: ContentStatus;
    isMlEnabled?: boolean;
    initialSmsMlJson?: Record<string, any>;
};

const useSmsForm = ({
    initialApplicationsAccess = [ApplicationAccessType.OUTREACH],
    initialName = 'Untitled',
    initialCommunicationType = CommunicationType.COMMERCIAL,
    initialContent = '',
    initialAutoResponses,
    isSavedSmsContent,
    isSavedSmsContentLoading,
    initialStatus = ContentStatus.DRAFT,
    isMlEnabled = false,
    initialSmsMlJson,
}: UseSmsFormProps) => {
    // Form state
    const [name, setName] = useState(initialName);
    const [contentState, setContentState] = useState(initialContent);
    const [autoResponsesState, setAutoResponsesState] = useState(initialAutoResponses);
    const [isNameTouched, setIsNameTouched] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [communicationType, setCommunicationType] = useState(initialCommunicationType);
    const [applicationsAccess, setApplicationsAccess] = useState(initialApplicationsAccess);

    // Multilingual state
    const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE_CODE);
    const [languages, setLanguages] = useState<string[]>([DEFAULT_LANGUAGE_CODE]);
    const [contentByLanguage, setContentByLanguage] = useState<Record<string, string>>({
        [DEFAULT_LANGUAGE_CODE]: initialContent,
    });
    const [autoResponsesByLanguage, setAutoResponsesByLanguage] = useState<Record<string, SMSAutoResponse[]>>({
        [DEFAULT_LANGUAGE_CODE]: initialAutoResponses ?? [],
    });

    // Sync form state when saved SMS data loads
    useEffect(() => {
        if (!isSavedSmsContent || isSavedSmsContentLoading) return;
        setName(initialName ?? 'Untitled');
        setContentState(initialContent ?? '');
        if (initialAutoResponses != null) {
            setAutoResponsesState(initialAutoResponses);
        }
    }, [isSavedSmsContent, isSavedSmsContentLoading, initialName, initialContent, initialAutoResponses]);

    // Hydrate multilingual maps from saved data (flag-gated)
    useEffect(() => {
        if (!isMlEnabled || !isSavedSmsContent || isSavedSmsContentLoading) return;
        if (initialSmsMlJson && Object.keys(initialSmsMlJson).length) {
            const savedLangs = Object.keys(initialSmsMlJson);
            setLanguages(savedLangs);
            const contentMap: Record<string, string> = {};
            const autoResponsesMap: Record<string, SMSAutoResponse[]> = {};
            for (const lang of savedLangs) {
                contentMap[lang] = initialSmsMlJson[lang]?.content?.text ?? '';
                autoResponsesMap[lang] = initialSmsMlJson[lang]?.autoResponses ?? [];
            }
            setContentByLanguage(contentMap);
            setAutoResponsesByLanguage(autoResponsesMap);
        } else {
            // No multilingual data yet — seed with the single-language content
            setContentByLanguage({ [DEFAULT_LANGUAGE_CODE]: initialContent ?? '' });
            setAutoResponsesByLanguage({ [DEFAULT_LANGUAGE_CODE]: initialAutoResponses ?? [] });
        }
    }, [
        isMlEnabled,
        isSavedSmsContent,
        isSavedSmsContentLoading,
        initialSmsMlJson,
        initialContent,
        initialAutoResponses,
    ]);

    // Wrapped setContent: keeps the per-language map in sync when ml is active
    const setContent = useCallback(
        (newContent: string) => {
            setContentState(newContent);
            if (isMlEnabled) {
                setContentByLanguage((prev) => ({ ...prev, [selectedLanguage]: newContent }));
            }
        },
        [isMlEnabled, selectedLanguage]
    );

    // Wrapped setAutoResponses: keeps the per-language map in sync when ml is active
    const setAutoResponses = useCallback(
        (newAutoResponses: SMSAutoResponse[] | undefined) => {
            setAutoResponsesState(newAutoResponses);
            if (isMlEnabled) {
                setAutoResponsesByLanguage((prev) => ({
                    ...prev,
                    [selectedLanguage]: newAutoResponses ?? [],
                }));
            }
        },
        [isMlEnabled, selectedLanguage]
    );

    // Switch to a different language: flush current content → load new language content
    const handleSelectLanguage = useCallback(
        (lang: string) => {
            const newContentByLang = { ...contentByLanguage, [selectedLanguage]: contentState };
            const newAutoResponsesByLang = {
                ...autoResponsesByLanguage,
                [selectedLanguage]: autoResponsesState ?? [],
            };
            setContentByLanguage(newContentByLang);
            setAutoResponsesByLanguage(newAutoResponsesByLang);
            setSelectedLanguage(lang);
            const rawContent = newContentByLang[lang];
            setContentState(typeof rawContent === 'string' ? rawContent : '');
            setAutoResponsesState(newAutoResponsesByLang[lang] ?? []);
        },
        [selectedLanguage, contentState, autoResponsesState, contentByLanguage, autoResponsesByLanguage]
    );

    const handleAddLanguage = useCallback(
        (lang: string) => {
            setLanguages((prev) => [...prev, lang]);
            // Seed with current content so the translator has a starting point,
            // and so handleSelectLanguage always finds a non-empty entry (prevents
            // the new language from accidentally sharing state with en-US).
            setContentByLanguage((prev) => ({ ...prev, [lang]: contentState }));
            setAutoResponsesByLanguage((prev) => ({ ...prev, [lang]: autoResponsesState ?? [] }));
        },
        [contentState, autoResponsesState]
    );

    const handleDeleteLanguage = useCallback(
        (lang: string) => {
            setLanguages((prev) => prev.filter((l) => l !== lang));
            setContentByLanguage((prev) => {
                const next = { ...prev };
                delete next[lang];
                return next;
            });
            setAutoResponsesByLanguage((prev) => {
                const next = { ...prev };
                delete next[lang];
                return next;
            });
            // If currently viewing the deleted language, fall back to en-US
            if (selectedLanguage === lang) {
                setSelectedLanguage(DEFAULT_LANGUAGE_CODE);
                setContentState(
                    typeof contentByLanguage[DEFAULT_LANGUAGE_CODE] === 'string'
                        ? contentByLanguage[DEFAULT_LANGUAGE_CODE]
                        : ''
                );
                setAutoResponsesState(autoResponsesByLanguage[DEFAULT_LANGUAGE_CODE] ?? []);
            }
        },
        [selectedLanguage, contentByLanguage, autoResponsesByLanguage]
    );

    // Validation
    const isNameUndefinedOnTouch = !name && isNameTouched;
    const isNameTooShort = isNameTouched && name.length < 3;

    // Helper messages
    const nameHelperText = isNameUndefinedOnTouch
        ? 'Please enter an SMS name'
        : isNameTooShort
        ? 'SMS name must have at least 3 characters'
        : '';

    // Input handlers
    const handleChangeName = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newName = e.target.value;
            setName(newName);
            if (!isNameTouched) setIsNameTouched(true);
        },
        [isNameTouched]
    );

    const handleCommunicationTypeChange = (e: any) => {
        setCommunicationType(e.target.value);
    };

    const handleApplicationsAccessChange = (e: any) => {
        setApplicationsAccess(e.target.value);
    };

    return {
        applicationsAccess,
        name,
        communicationType,
        content: contentState,
        autoResponses: autoResponsesState,
        isNameUndefinedOnTouch,
        isNameTooShort,
        nameHelperText,
        errorMessage,
        setContent,
        setAutoResponses,
        setErrorMessage,
        handleChangeName,
        handleApplicationsAccessChange,
        handleCommunicationTypeChange,
        // Multilingual
        selectedLanguage,
        languages,
        contentByLanguage,
        autoResponsesByLanguage,
        handleSelectLanguage,
        handleAddLanguage,
        handleDeleteLanguage,
    };
};

export default useSmsForm;
